<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';
import Spinner from '@components/Spinner.vue';
import IconX from '@icons/ph_x.svg';
import Icon from '@components/Icon.vue';
import Button from '@components/Button.vue';
import { eventBus } from '@tools/event-bus';
import { useLoading } from '@composables/useLoading.ts';
import { SelectionItemEvent, Slider } from '@enums/event.ts';
import { apiRemoveSelectionItem, type RemoveSelectionItemParams } from '@api/selectionItem/apiSelectionItem';
import { useSelectionItemStore } from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import { storeToRefs } from 'pinia';
import { trans } from '@elements/translation';
import { OFFER_EVENT } from '../../constants';

const props = defineProps<{ selectionItemId: number }>();

const { isLoading, startLoading, stopLoading } = useLoading();

const selectionItemStore = useSelectionItemStore();

const { removeSelectionItem } = selectionItemStore;

const { accountManager } = storeToRefs(selectionItemStore);

const openRefuseSelectionItemSlidein = (event: MouseEvent): void => {
  event.stopPropagation();

  startLoading();
  eventBus.dispatchEvent(
    new CustomEvent(OFFER_EVENT.SLIDER_SHOW, {
      detail: {
        sliderType: Slider.REFUSE_SELECTION_ITEM,
        sliderPayload: {
          id: props.selectionItemId,
          accountManager: accountManager.value,
        },
      },
    }),
  );
};

const handleRemoveSelectionItem = async (event: Event): Promise<void> => {
  const { selectionItemId, refusalReasons, refusedByUser } = event.detail as RemoveSelectionItemParams;

  const isRemoved = await apiRemoveSelectionItem({ selectionItemId, refusalReasons, refusedByUser });

  if (!isRemoved) {
    stopLoading();
    return;
  }

  removeSelectionItem({ selectionItemId, refusalReasons, refusedByUser });

  stopLoading();
};

const handleStopLoading = (event: Event): void => {
  if (event.detail.selectionItemId !== props.selectionItemId) {
    return;
  }

  stopLoading();
};

onMounted(() => {
  eventBus.addEventListener(SelectionItemEvent.REMOVE, handleRemoveSelectionItem);
  eventBus.addEventListener(SelectionItemEvent.REMOVE_LOADER, handleStopLoading);
});

onBeforeUnmount(() => {
  eventBus.removeEventListener(SelectionItemEvent.REMOVE, handleRemoveSelectionItem);
  eventBus.removeEventListener(SelectionItemEvent.REMOVE_LOADER, handleStopLoading);
});
</script>

<template>
  <Button
    class="selection-item-delete-button"
    :disabled="isLoading"
    @click="openRefuseSelectionItemSlidein"
  >
    <spinner v-if="isLoading" />

    <template v-else>
      <Icon
        :svg="IconX"
        width="24"
        height="24"
        fill="red"
      />

      <span>
        {{ trans('selection_item.actions.button.archive') }}
      </span>
    </template>
  </button>
</template>
